import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { TombstoneLayout } from '@/components/TombstoneLayout';
import { useUserEvents } from '@/hooks/useTombstone';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LoginArea } from '@/components/auth/LoginArea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { VanishDialog } from '@/components/VanishDialog';
import {
  Archive,
  AlertTriangle,
  Globe,
  Server,
  Info,
} from 'lucide-react';

export default function MasterTombstone() {
  useSeoMeta({
    title: 'Master Tombstone — Tombstone',
    description: 'Your canonical master deletion record and NIP-62 vanish controls.',
  });

  const { user } = useCurrentUser();
  const { data } = useUserEvents(user?.pubkey);
  const { mutate: publishEvent } = useNostrPublish();
  const { toast } = useToast();

  const [generatedMaster, setGeneratedMaster] = useState<Record<string, unknown> | null>(null);
  const [vanishOpen, setVanishOpen] = useState(false);

  const master = data?.masterTombstone;
  const deletionEvents = data?.deletionEvents || [];

  const handleGenerateMaster = () => {
    if (!deletionEvents.length) {
      toast({ title: 'No deletions found', description: 'Publish some deletion requests first.' });
      return;
    }

    const allE = new Set<string>();
    const allA = new Set<string>();
    const allKinds = new Set<string>();

    deletionEvents.forEach((del) => {
      del.tags.forEach((tag) => {
        if (tag[0] === 'e' && tag[1]) allE.add(tag[1]);
        if (tag[0] === 'a' && tag[1]) allA.add(tag[1]);
        if (tag[0] === 'k' && tag[1]) allKinds.add(tag[1]);
      });
    });

    const masterEvent = {
      kind: 30078,
      content: JSON.stringify({
        type: 'tombstone-master',
        totalReferences: allE.size + allA.size,
        generatedAt: Date.now(),
        summary: `Master record of ${allE.size + allA.size} deleted events`,
      }),
      tags: [
        ['d', 'tombstone.master'],
        ['alt', 'Tombstone Master Deletion Record'],
        ...Array.from(allE).map((id) => ['e', id]),
        ...Array.from(allA).map((coord) => ['a', coord]),
        ...Array.from(allKinds).map((k) => ['k', k]),
      ],
    };

    setGeneratedMaster(masterEvent);

    toast({
      title: 'Master generated',
      description: `Created master with ${allE.size + allA.size} references. Review below before publishing.`,
    });
  };

  const handlePublishMaster = () => {
    if (!generatedMaster) return;
    publishEvent(generatedMaster as Parameters<typeof publishEvent>[0], {
      onSuccess: () => {
        toast({
          title: 'Master Tombstone published',
          description: 'Kind 30078 event published to your write relays.',
        });
        setGeneratedMaster(null);
      },
      onError: (err) => {
        toast({
          title: 'Publish failed',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      },
    });
  };

  if (!user) {
    return (
      <TombstoneLayout>
        <div className="max-w-3xl mx-auto px-6 py-20 text-center space-y-6">
          <h1 className="font-display text-2xl font-semibold text-foreground">Master Tombstone</h1>
          <p className="text-muted-foreground">Log in to access your master deletion record and vanish controls.</p>
          <LoginArea className="max-w-56 mx-auto" />
        </div>
      </TombstoneLayout>
    );
  }

  return (
    <TombstoneLayout>
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Header */}
        <div>
          <h1 className="font-display text-2xl font-semibold text-foreground tracking-tight">
            Master Tombstone
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Canonical deletion record and advanced vanish controls.
          </p>
        </div>

        {/* ── Master Tombstone Record (NIP-78) ── */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Archive className="w-4 h-4 text-accent" />
            <h2 className="font-display font-semibold text-foreground text-sm tracking-wide uppercase">
              Master Deletion Record — NIP-78
            </h2>
          </div>

          <Card className="border-border bg-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
                Current Master Record
              </CardTitle>
              <CardDescription className="text-xs leading-relaxed">
                A kind 30078 event (<code>d="tombstone.master"</code>) that aggregates all your deletion
                references into a single canonical record. Acts as a source of truth for your deletion
                history and does not interfere with individual kind 5 requests.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {master ? (
                <div className="bg-secondary/50 p-4 rounded-xl font-mono text-xs overflow-auto max-h-64 border border-border">
                  <pre>{JSON.stringify(master, null, 2)}</pre>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-border py-8 text-center">
                  <p className="text-sm text-muted-foreground">No master tombstone event found.</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Generate one below to create your canonical deletion record.
                  </p>
                </div>
              )}

              <div className="rounded border border-border bg-secondary/20 p-3 space-y-1.5 text-xs text-muted-foreground">
                <p className="font-medium text-foreground">What the master record contains</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>All event IDs (<code>e</code> tags) from your kind 5 deletion requests</li>
                  <li>All address coordinates (<code>a</code> tags) for addressable events</li>
                  <li>All referenced kinds (<code>k</code> tags)</li>
                  <li>Published as kind 30078 with <code>d="tombstone.master"</code></li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleGenerateMaster}
                  variant="outline"
                  className="flex-1"
                >
                  Generate Master Event
                </Button>
                {generatedMaster && (
                  <Button
                    onClick={handlePublishMaster}
                    className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
                  >
                    Publish to Relays
                  </Button>
                )}
              </div>

              {generatedMaster && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Generated Event Preview</span>
                    <span className="text-[10px] text-muted-foreground font-mono">kind 30078</span>
                  </div>
                  <div className="bg-secondary/50 p-4 rounded-xl font-mono text-xs overflow-auto max-h-80 border border-border">
                    <pre>{JSON.stringify(generatedMaster, null, 2)}</pre>
                  </div>
                  <p className="text-[10px] text-muted-foreground text-center">
                    Review the event above before publishing. Click "Publish to Relays" to broadcast.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <div className="border-t border-border/50" />

        {/* ── NIP-62: Request to Vanish ── */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <img src="/tombstone_skull.png" alt="" className="w-4 h-4 object-contain" />
            <h2 className="font-display font-semibold text-foreground text-sm tracking-wide uppercase">
              Request to Vanish — NIP-62
            </h2>
          </div>

          {/* Explanation */}
          <div className="rounded-lg border border-accent/25 bg-background p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
              <div className="space-y-1.5 text-sm">
                <p className="font-medium text-foreground">This goes further than NIP-09 deletion.</p>
                <p className="text-muted-foreground leading-relaxed text-xs">
                  A <strong className="text-foreground">Request to Vanish</strong> (kind 62) asks a relay to
                  permanently delete <em>everything</em> from your public key — all events, all DMs (gift
                  wraps), and any future re-broadcasts. It is legally binding in some jurisdictions (e.g. GDPR
                  right to erasure). Relay compliance is voluntary but those that support NIP-62 treat this as
                  irreversible.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="rounded border border-accent/20 bg-secondary/30 p-3 space-y-1.5">
                <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                  <Server className="w-3.5 h-3.5 text-accent" />
                  Per-Relay Vanish
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Target specific relays. The kind 62 event is sent directly to each relay with its
                  URL in the <code className="text-accent">relay</code> tag.
                </p>
              </div>
              <div className="rounded border border-accent/20 bg-secondary/30 p-3 space-y-1.5">
                <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                  <Globe className="w-3.5 h-3.5 text-accent" />
                  Global Vanish
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Uses the <code className="text-accent">ALL_RELAYS</code> sentinel and broadcasts to
                  all known relays. Use this for a complete digital erasure request.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 text-[11px] text-muted-foreground pt-1">
              <Info className="w-3 h-3 flex-shrink-0 mt-0.5 text-accent" />
              <span>
                NIP-62 is a <strong>draft</strong> NIP. Most relays do not yet enforce it. Sending this
                request is future-proof and legally meaningful, but current relay support is limited.
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="border-accent/60 text-accent bg-background hover:bg-accent/10 hover:border-accent hover:text-accent tombstone-glow w-full sm:w-auto"
            onClick={() => setVanishOpen(true)}
          >
            <img src="/tombstone_skull.png" alt="" className="w-4 h-4 object-contain mr-2" />
            Open Vanish Controls
          </Button>
        </section>
      </div>

      <VanishDialog open={vanishOpen} onOpenChange={setVanishOpen} />
    </TombstoneLayout>
  );
}
